import { Box, Stack } from '@ltvco/refresh-lib/theme';
import { useScreenSize } from '@ltvco/refresh-lib/utils';
import {
  CHATAI_LOADING_STATUS,
  Message,
  MessagesContentStack,
  TypingIndicator,
} from '@ltvco/refresh-lib/v2';
import { useRef, useEffect, useCallback, useState, useMemo } from 'react';

import { MessagePair } from '../../components/MessagePair';
import { RetrySearchInstruction } from '../../components/RetrySearchInstruction';
import {
  useScrollManagement,
  type MessagePair as MessagePairForHooks,
} from '../../hooks/useScrollManagement';
import { useStickyHeader } from '../../hooks/useStickyHeader';
import { useTailGap } from '../../hooks/useTailGap';
import { useMessagePairing } from '../hooks/useMessagePairing';

import { VehicleMatchmakerContent } from './VehicleMatchmakerContent';

import type { VehicleMatch } from '@ltvco/refresh-lib/api';

export interface LoadingStatus {
  message: string;
  subtitle: string;
}

type MessagesContainerProps = {
  messages: Message[];
  loadingStatus: LoadingStatus;
  onSendMessage?: (message?: string) => void;
  onNewSearch: () => void;
  shouldResetInputs: boolean;
  onResetComplete: () => void;
  onOpenVehicleReport?: (match: VehicleMatch) => Promise<void>;
  openReportMatchKey?: string | null;
};

export const MessagesContainer = ({
  messages,
  loadingStatus,
  onSendMessage,
  onNewSearch,
  shouldResetInputs,
  onResetComplete,
  onOpenVehicleReport,
  openReportMatchKey,
}: MessagesContainerProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { isMobile } = useScreenSize();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleProcessingStateChange = useCallback((processing: boolean) => {
    setIsProcessing(processing);
  }, []);

  useEffect(() => {
    if (messages.length === 0) {
      setIsProcessing(false);
    }
  }, [messages.length]);

  // Refs for layout/scroll
  const scrollRootRef = useRef<HTMLDivElement | null>(null);
  const messageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const gapRef = useRef<HTMLDivElement | null>(null);
  const typingRef = useRef<HTMLDivElement | null>(null);

  // Use custom hooks for complex logic
  const {
    pairs,
    firstMatchmakerAssistantIndex,
    firstMatchmakerData,
    hasError,
  } = useMessagePairing(messages);

  // Shape expected by shared hooks (user, assistant, userIndex, assistantIndex)
  const pairsForHooks: MessagePairForHooks[] = useMemo(
    () =>
      pairs.map((p) => ({
        user: p.user,
        assistant: p.assistant,
        userIndex: p.userIndex,
        assistantIndex: p.assistantIndex,
      })),
    [pairs],
  );

  useEffect(() => {
    if (
      isProcessing &&
      loadingStatus.message === CHATAI_LOADING_STATUS.IDLE.message &&
      (firstMatchmakerData || hasError)
    ) {
      setIsProcessing(false);
    }
  }, [isProcessing, loadingStatus.message, firstMatchmakerData, hasError]);

  const { hasUserScrolled } = useScrollManagement({
    scrollRootRef,
    messageRefs,
    pairs: pairsForHooks,
    messagesLength: messages.length,
    loadingMessage: loadingStatus.message,
    featureTitle: 'Vehicle Matchmaker',
  });

  const stickyIndex = useStickyHeader({
    scrollRootRef,
    messageRefs,
    pairs: pairsForHooks,
    messages,
  });

  const tailGap = useTailGap({
    scrollRootRef,
    messageRefs,
    gapRef,
    typingRef,
    pairs: pairsForHooks,
    loadingMessage: loadingStatus.message,
    hasError,
    maxTailGap: 80,
    featureTitle: 'Vehicle Matchmaker',
  });

  const isContextLoading =
    loadingStatus.message !== CHATAI_LOADING_STATUS.IDLE.message;
  const isLoading = isProcessing || isContextLoading;

  // Cleanup timeout on component unmount to prevent memory leaks
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <MessagesContentStack
      ref={scrollRootRef}
      width="100%"
      spacing={2}
      paddingBottom={isMobile ? '130px' : '20px'}
      height={isMobile ? '100%' : 'auto'}
      minHeight={isMobile ? 0 : 'auto'}
      flexGrow={1}
      sx={{
        overflowY: 'auto',
      }}
    >
      <VehicleMatchmakerContent
        onSendMessage={onSendMessage}
        firstMatchmakerData={firstMatchmakerData}
        shouldResetInputs={shouldResetInputs}
        onResetComplete={onResetComplete}
        onProcessingStateChange={handleProcessingStateChange}
        hasError={hasError}
        onNewSearch={onNewSearch}
        isLoading={isLoading}
        onOpenVehicleReport={onOpenVehicleReport}
        openReportMatchKey={openReportMatchKey}
      />
      {isProcessing && pairs.length === 0 && (
        <Box ref={typingRef} sx={{ width: '100%', maxWidth: '740px' }}>
          <TypingIndicator loadingStatus={loadingStatus} />
        </Box>
      )}
      {pairs.map((pair, index) => (
        <MessagePair
          key={pair.id}
          userMessage={pair.user}
          assistantMessage={pair.assistant}
          userIndex={pair.userIndex}
          assistantIndex={pair.assistantIndex}
          isLastPair={index === pairs.length - 1}
          isSticky={stickyIndex === pair.userIndex}
          hasUserScrolled={hasUserScrolled}
          loadingStatus={loadingStatus}
          firstEstimateAssistantIndex={firstMatchmakerAssistantIndex}
          tailGap={tailGap}
          messageRefs={messageRefs}
          typingRef={typingRef}
          gapRef={gapRef}
        />
      ))}
      {loadingStatus.message === CHATAI_LOADING_STATUS.IDLE.message &&
        hasError &&
        pairs.length > 0 && (
          <Stack width="100%" justifyContent="center" alignItems="center">
            <RetrySearchInstruction onRetrySearch={onNewSearch} />
          </Stack>
        )}
    </MessagesContentStack>
  );
};
